// src/utils/knowledgeFreshness.js
//
// Pure display helpers for the KnowledgePage.jsx freshness UI (WP8,
// docs/launch-signoff-spec.md §9). Kept separate from the page component so
// the label/color logic is unit-testable without pulling in React/JSX or the
// runtime knowledge-loading side effects the page itself has.
//
// Inputs are the pack-status entries from GET /proxy/knowledge/status
// (proxy-server/knowledge/freshness.js's `packStatus()` — { id, name,
// version, lastUpdated, lastVerified, staleDays, stale, overridden,
// sourceUrls }) and the change entries inside a pending proposal from
// GET /proxy/knowledge/proposals ({ kind: 'add'|'update'|'remove'|'note', ... }).

/**
 * formatFreshnessBadge(status) -> { label, tone }
 * tone is one of 'ok' | 'stale' | 'unknown' — the page maps tone to color.
 *
 * `status` may be null/undefined/malformed (e.g. the proxy hasn't answered
 * yet, or a proposal references a pack no longer in packStatus()) — always
 * returns a renderable badge rather than throwing.
 */
export function formatFreshnessBadge(status) {
  if (!status || typeof status !== 'object') {
    return { label: 'freshness unknown', tone: 'unknown' };
  }
  const { stale, staleDays } = status;
  if (staleDays === null || staleDays === undefined || !Number.isFinite(Number(staleDays))) {
    return { label: 'freshness unknown', tone: 'unknown' };
  }
  const days = Number(staleDays);
  if (!stale) {
    return { label: days === 0 ? 'verified today' : `verified ${days}d ago`, tone: 'ok' };
  }
  return { label: `stale (${days}d)`, tone: 'stale' };
}

/**
 * changeKindMeta(kind) -> { label, tone }
 * tone maps to a chip color the page owns (add=green/update=amber/
 * remove=red/note=grey). Unknown/malformed kinds fall back to the 'note'
 * (grey) tone rather than throwing, so a change entry with a bad `kind`
 * still renders instead of breaking the review card.
 */
export function changeKindMeta(kind) {
  switch (kind) {
    case 'add':    return { label: 'add',    tone: 'add' };
    case 'update': return { label: 'update', tone: 'update' };
    case 'remove': return { label: 'remove', tone: 'remove' };
    case 'note':   return { label: 'note',   tone: 'note' };
    default:       return { label: String(kind || 'note'), tone: 'note' };
  }
}
